import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Heart, ChevronLeft, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { FALLBACK_IMAGE, imageFallback, resolveImageUrl } from '../lib/imageUtils';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/products/${id}`);
      setProduct(response.data);
      
      if (response.data.category_id) {
        const relatedRes = await axios.get(`${BACKEND_URL}/api/products?category_id=${response.data.category_id}&limit=5`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== id).slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A3A3A3]">Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A3A3A3]">Product not found</p>
      </div>
    );
  }

  const images = product.images && product.images.length > 0
    ? product.images.map(img => resolveImageUrl(img, BACKEND_URL))
    : [FALLBACK_IMAGE];

  const hasDiscount = product.offer_applied || product.final_price < product.price;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/products" className="inline-flex items-center space-x-2 text-[#A3A3A3] hover:text-white mb-8 transition-colors" data-testid="back-to-products-link">
          <ChevronLeft size={20} />
          <span className="text-sm uppercase tracking-wider">Back to Collection</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          <div className="space-y-4">
            <div className="aspect-[4/5] bg-[#141414] overflow-hidden">
              <img
                src={images[selectedImage]}
                alt={product.name}
                onError={imageFallback}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square bg-[#141414] overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-[#D4AF37]' : 'border-transparent hover:border-white/20'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} onError={imageFallback} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]" data-testid="product-category">
                {product.subcategory_name || product.category_name}
              </p>
              <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white" data-testid="product-detail-name">
                {product.name}
              </h1>
            </div>

            <div className="flex items-baseline space-x-4">
              <span className="text-4xl font-light text-[#D4AF37]" data-testid="product-detail-price">
                ₹{product.final_price.toLocaleString()}
              </span>
              {hasDiscount && (
                <>
                  <span className="text-xl text-[#A3A3A3] line-through">
                    ₹{product.price.toLocaleString()}
                  </span>
                  <span className="px-3 py-1 bg-[#D4AF37] text-black text-xs font-medium uppercase">
                    {product.discount_type === 'percentage' ? `${product.discount_value}% OFF` : `₹${product.discount_value} OFF`}
                  </span>
                </>
              )}
            </div>

            <p className="text-[#A3A3A3] leading-relaxed" data-testid="product-detail-description">
              {product.description}
            </p>

            {product.stock > 0 ? (
              <>
                <div className="space-y-3">
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block">Quantity</label>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-[#141414] border border-white/20 hover:border-white flex items-center justify-center transition-colors"
                      data-testid="decrease-quantity-button"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="text-xl font-light w-12 text-center" data-testid="quantity-display">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="w-10 h-10 bg-[#141414] border border-white/20 hover:border-white flex items-center justify-center transition-colors"
                      data-testid="increase-quantity-button"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  {product.stock < 10 && (
                    <p className="text-xs text-[#FF3B30] uppercase tracking-wider">
                      Only {product.stock} left in stock
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300 flex items-center justify-center space-x-2"
                    data-testid="add-to-cart-detail-button"
                  >
                    <ShoppingCart size={18} />
                    <span>Add to Cart</span>
                  </button>
                  <button
                    onClick={() => toggleWishlist(product)}
                    className={`px-6 py-4 border transition-all duration-300 flex items-center justify-center space-x-2 ${
                      isInWishlist(product.id)
                        ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                        : 'bg-transparent text-white border-white/20 hover:border-white'
                    }`}
                    data-testid="wishlist-detail-button"
                  >
                    <Heart size={18} className={isInWishlist(product.id) ? 'fill-current' : ''} />
                  </button>
                </div>
              </>
            ) : (
              <div className="py-8 border border-white/10 text-center">
                <p className="text-[#A3A3A3] uppercase tracking-wider" data-testid="out-of-stock-message">Out of Stock</p>
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <section data-testid="related-products-section">
            <h2 className="text-3xl font-light tracking-tighter text-white mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(prod => (
                <ProductCard key={prod.id} product={prod} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
